/**
 * Service for managing student history filter options in Firestore
 * Provides CRUD operations and caching for student history filter configuration
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase.js';

class StudentHistoryFiltersService {
  constructor() {
    this.collectionName = 'studentHistoryFilters';
    this.cache = null;
    this.cacheTimestamp = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get collection reference
   */
  getCollectionRef() {
    return collection(db, this.collectionName);
  }

  /**
   * Check if cache is valid
   */
  isCacheValid() {
    if (!this.cache || !this.cacheTimestamp) return false;
    return Date.now() - this.cacheTimestamp < this.cacheDuration;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Get all student history filters (with caching)
   * @returns {Promise<Array>} Array of filter objects
   */
  async getAll() {
    // Return cached data if valid
    if (this.isCacheValid()) {
      return this.cache;
    }

    try {
      const q = query(this.getCollectionRef(), orderBy('sortOrder', 'asc'));
      const snapshot = await getDocs(q);

      const filters = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(filter => filter.active !== false);

      // Update cache
      this.cache = filters;
      this.cacheTimestamp = Date.now();

      return filters;
    } catch (error) {
      console.error('Error fetching student history filters:', error);
      throw error;
    }
  }

  /**
   * Get all filters including inactive ones
   * @returns {Promise<Array>} Array of all filter objects
   */
  async getAllIncludingInactive() {
    try {
      const q = query(this.getCollectionRef(), orderBy('sortOrder', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all student history filters:', error);
      throw error;
    }
  }

  /**
   * Get single filter by ID
   * @param {string} id - Document ID
   * @returns {Promise<Object|null>} Filter object or null
   */
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching student history filter:', error);
      throw error;
    }
  }

  /**
   * Get filter by value field
   * @param {string} value - The value field to search for
   * @returns {Promise<Object|null>} Filter object or null
   */
  async getByValue(value) {
    const filters = await this.getAll();
    return filters.find(filter => filter.value === value) || null;
  }

  /**
   * Create a new filter
   * @param {Object} data - Filter data
   * @returns {Promise<string>} New document ID
   */
  async create(data) {
    try {
      const docData = {
        ...data,
        active: data.active !== undefined ? data.active : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(), docData);
      this.clearCache();
      return docRef.id;
    } catch (error) {
      console.error('Error creating student history filter:', error);
      throw error;
    }
  }

  /**
   * Update an existing filter
   * @param {string} id - Document ID
   * @param {Object} data - Updated data
   * @returns {Promise<void>}
   */
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      this.clearCache();
    } catch (error) {
      console.error('Error updating student history filter:', error);
      throw error;
    }
  }

  /**
   * Delete a filter
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      this.clearCache();
    } catch (error) {
      console.error('Error deleting student history filter:', error);
      throw error;
    }
  }

  /**
   * Soft delete - mark as inactive
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async deactivate(id) {
    await this.update(id, { active: false });
  }

  /**
   * Reactivate a filter
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async activate(id) {
    await this.update(id, { active: true });
  }

  /**
   * Seed initial filters from hardcoded data
   * @param {Array} filters - Array of filter objects
   * @returns {Promise<void>}
   */
  async seedInitialData(filters) {
    try {
      const batch = writeBatch(db);

      filters.forEach((filterData, index) => {
        const docRef = doc(this.getCollectionRef());
        batch.set(docRef, {
          ...filterData,
          sortOrder: filterData.sortOrder !== undefined ? filterData.sortOrder : index,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache();
      console.log(`Successfully seeded ${filters.length} student history filters`);
    } catch (error) {
      console.error('Error seeding student history filters:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const studentHistoryFiltersService = new StudentHistoryFiltersService();
export default studentHistoryFiltersService;
